import { DropDownList } from "@repo/ui";
import { useMemo } from "react";

import type { DefinitionNode, DefinitionTree } from "../settingsTree";

import { containerOptions, settingOptions } from "../settingsTree";
import style from "./style.module.scss";

interface Props {
	tree: DefinitionTree;
	/** FullNames of the selected containers, top level first */
	path: string[];
	/** FullName of the selected setting */
	leaf?: string;
	onChange: (path: string[], leaf?: string) => void;
	/** Shown in an empty dropdown; defaults to "All", for filtering */
	placeholder?: string;
}

const toOption = (node: DefinitionNode) => ({
	value: node.definition.fullName,
	label: node.definition.displayName || node.definition.name,
});

/**
 * One dropdown per level of the definition tree, down to the deepest selected container,
 * then a Setting dropdown with the settings at that level.
 * Renders a fragment so each dropdown takes its own cell in the filter bar.
 */
export function DefinitionCascade({ tree, path, leaf, onChange, placeholder = "All" }: Props) {
	const levels = useMemo(() => {
		const result: { value: string; label: string }[][] = [];
		for (let i = 0; i <= path.length; i++) {
			const options = containerOptions(tree, path[i - 1]).map(toOption);
			if (!options.length) break;
			result.push(options);
		}
		return result;
	}, [tree, path]);

	const settings = useMemo(() => settingOptions(tree, path[path.length - 1]).map(toOption), [tree, path]);

	return (
		<>
			{levels.map((options, i) => (
				<DropDownList
					key={i}
					label={i === 0 ? "System" : "Group"}
					placeholder={placeholder}
					values={options}
					value={path[i] ?? null}
					clearable
					fullLength
					optionClassName={style.singleLineOption}
					changeHandler={(value: string) => onChange(value ? [...path.slice(0, i), value] : path.slice(0, i))}
				/>
			))}
			{settings.length > 0 && (
				<DropDownList
					label="Setting"
					placeholder={placeholder}
					values={settings}
					value={leaf ?? null}
					clearable
					fullLength
					optionClassName={style.singleLineOption}
					changeHandler={(value: string) => onChange(path, value || undefined)}
				/>
			)}
		</>
	);
}
